import type { ExpertReportQueueStatusKey } from "./types";

/**
 * TUT-480 (herdado da TUT-476) — o recorte da aba Laudos da Elaboração por
 * ETAPA, por REVISOR e por DATA DE PROTOCOLO, na URL e no servidor.
 *
 * É o que dá destino à "Fila de laudos" e à "Medição de laudos" da Visão
 * geral: o número do widget abre a Elaboração com o mesmo recorte com que foi
 * contado (`/elaboracao?atividade=laudos&etapa=filed&periodoDe=…&periodoAte=…`).
 * Aqui mora só a FORMA — o catálogo das etapas, a leitura saneada da URL e o
 * texto do que está ligado; a regra de fronteira do período é do servidor
 * (`apps/api/src/domain/recorte-de-laudos.ts`).
 *
 * `etapa` repete na chave (`?etapa=drafting&etapa=in_review`), como todo filtro
 * de catálogo da casa (P30). `periodoDe` e `periodoAte` são dias de calendário
 * `AAAA-MM-DD` no fuso do escritório, inclusivos; o mesmo par de nomes que o
 * recorte dos widgets usa nas listas de prazos, perícias e processos, lido pelo
 * `periodoDaBusca` de `periodo-de-datas.ts` (o único módulo de período).
 */
export const ETAPAS_DE_LAUDO = [
  { chave: "completed", rotulo: "Perícia realizada", status: "COMPLETED" },
  { chave: "drafting", rotulo: "Em elaboração", status: "DRAFTING" },
  { chave: "in_review", rotulo: "Aguardando revisão", status: "IN_REVIEW" },
  { chave: "awaiting_filing", rotulo: "Aguardando protocolo", status: "AWAITING_FILING" },
  { chave: "filed", rotulo: "Protocolado", status: "FILED" },
] as const satisfies readonly {
  chave: string;
  rotulo: string;
  status: ExpertReportQueueStatusKey;
}[];

export type EtapaDeLaudo = (typeof ETAPAS_DE_LAUDO)[number]["chave"];

/**
 * As etapas da busca, saneadas: aceita texto único ou lista (a URL chega como
 * texto quando a chave aparece uma vez), descarta o que não é do catálogo e
 * repetições, e devolve na ordem do catálogo. Lista fechada, conferida no
 * ponto de leitura (TUT-204g).
 */
export function etapasDaBusca(valor: unknown): EtapaDeLaudo[] {
  const pedidas = new Set(
    (Array.isArray(valor) ? valor : [valor]).filter(
      (item): item is string => typeof item === "string",
    ),
  );
  return ETAPAS_DE_LAUDO.filter((etapa) => pedidas.has(etapa.chave)).map(
    (etapa) => etapa.chave,
  );
}

/** As etapas viram os `status` que a fila de laudos pede ao servidor. */
export function statusDasEtapas(
  etapas: readonly EtapaDeLaudo[],
): ExpertReportQueueStatusKey[] {
  return ETAPAS_DE_LAUDO.filter((etapa) => etapas.includes(etapa.chave)).map(
    (etapa) => etapa.status,
  );
}
